import type { KeyboardEvent, ReactNode } from "react";

import { Label } from "@pinatagrams/fiesta";

type CollectionLabelGroupProps = {
	emoji?: string | null;
	productBody: string;
	filling: string[];
	additions?: string[];
	addons?: string[];
	recipientName: string;
	recipientStreet1: string;
	message?: string | null;
	idNode: ReactNode;
	skuNode?: ReactNode;
	detailsAction?: ReactNode;
	outsideGraphicUrl?: string | null;
	outsideGraphicAlt: string;
	insideGraphicUrl?: string | null;
	insideGraphicAlt: string;
	onOutsideClick?: () => void;
	onInsideClick?: () => void;
	messageFontSizeCqi?: number;
	/** Custom-builder piñata (CUSTOM-* sku): the DETAILS card gets a red frame + CUSTOM chip so the build can't be mistaken for stock. */
	isCustom?: boolean;
};

const CUSTOM_ACCENT = "#C82020";

// The custom marker lives on the DETAILS card only — the graphic cards ARE
// the print files and stay untouched. LAYOUT-NEUTRAL by construction:
// print pagination is exact-fit arithmetic (@page = 8in x 4in, every label
// exactly page-sized, no break rules), so the frame must not add a single
// pixel to the card's box — an inset shadow on the Label itself draws
// inside its existing edges (and, unlike a wrapper shadow, isn't painted
// over by the card background).
const customDetailsCardStyle = {
	boxShadow: `inset 0 0 0 5px ${CUSTOM_ACCENT}`,
	// Keep the red edge on paper too — the print stack is where it matters.
	WebkitPrintColorAdjust: "exact",
	printColorAdjust: "exact",
} as const;

// In-flow chip leading the left section (before the body name), sized like
// the filling pills around it — it marks the row that lists what goes IN
// the box, and stays clear of the recipient name on the right.
const customChipStyle = {
	backgroundColor: CUSTOM_ACCENT,
	borderRadius: "999px",
	color: "#FFFFFF",
	fontSize: "0.75rem",
	fontWeight: 700,
	letterSpacing: "0.08em",
	padding: "2px 10px",
	whiteSpace: "nowrap",
	WebkitPrintColorAdjust: "exact",
	printColorAdjust: "exact",
} as const;

const productBodyLabel = (body: string): string => body.replace(/\s*pi[nñ]atagram\s*/gi, "");

const interactiveLabelProps = (onClick?: () => void) => {
	if (!onClick) {
		return {};
	}

	return {
		role: "button" as const,
		tabIndex: 0,
		onClick,
		onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				onClick();
			}
		},
		style: { cursor: "pointer" },
	};
};

/**
 * Shared three-card label group renderer used by collections print/editor and orders preview.
 */
export function CollectionLabelGroup({
	emoji,
	productBody,
	filling,
	additions,
	addons,
	recipientName,
	recipientStreet1,
	message,
	idNode,
	skuNode,
	detailsAction,
	outsideGraphicUrl,
	outsideGraphicAlt,
	insideGraphicUrl,
	insideGraphicAlt,
	onOutsideClick,
	onInsideClick,
	messageFontSizeCqi = 20,
	isCustom = false,
}: CollectionLabelGroupProps) {
	return (
		<div style={{ display: "inline-block", width: "fit-content" }}>
			<Label.Group>
				<Label style={isCustom ? customDetailsCardStyle : undefined}>
					<Label.Details>
						<Label.Section>
							{isCustom ? <span style={customChipStyle}>🎨 CUSTOM BUILD</span> : null}
							{skuNode ?? (
								<Label.Sku emoji={emoji ?? "🪅"}>{productBodyLabel(productBody)}</Label.Sku>
							)}
							{filling.map((value) => (
								<Label.Pill key={`filling-${value}`}>{value}</Label.Pill>
							))}
							{additions?.map((value) => (
								<Label.Pill key={`addition-${value}`}>{value}</Label.Pill>
							))}
							{addons?.map((value) => (
								<Label.Pill key={`addon-${value}`}>{value}</Label.Pill>
							))}
						</Label.Section>
						<Label.Divider highlight={filling.length > 0} />
						<Label.Section variant="message">
							<Label.Name>{recipientName}</Label.Name>
							<Label.Address>{recipientStreet1}</Label.Address>
							{message ? <Label.Message>{message}</Label.Message> : null}
							{detailsAction}
						</Label.Section>
						{idNode}
					</Label.Details>
				</Label>

				<Label {...interactiveLabelProps(onOutsideClick)}>
					{outsideGraphicUrl ? (
						<Label.Graphic src={outsideGraphicUrl} alt={outsideGraphicAlt} />
					) : (
						<Label.Details>
							<Label.Section variant="message">
								<Label.Name>Front graphic placeholder</Label.Name>
								<Label.Address>No graphic available</Label.Address>
							</Label.Section>
						</Label.Details>
					)}
				</Label>

				<Label {...interactiveLabelProps(onInsideClick)}>
					{insideGraphicUrl ? (
						<>
							<Label.Graphic src={insideGraphicUrl} alt={insideGraphicAlt} />
							<Label.MessageOverlay>
								<Label.MessageText style={{ fontSize: `${messageFontSizeCqi}cqi` }}>
									{message}
								</Label.MessageText>
							</Label.MessageOverlay>
						</>
					) : (
						<Label.Details>
							<Label.Section variant="message">
								<Label.Name>Message graphic placeholder</Label.Name>
								<Label.Address>No graphic available</Label.Address>
								{message ? <Label.Message>{message}</Label.Message> : null}
							</Label.Section>
						</Label.Details>
					)}
				</Label>
			</Label.Group>
		</div>
	);
}
